import {
  getCustomerCompanyInvitationListQueryKey,
  getCustomerMembershipActivityListQueryKey,
  useCustomerCompanyInvitationDestroy,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { LoaderCircleIcon, MailXIcon } from "lucide-react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

interface Props {
  companyUuid: string
  uuid: string
  email: string
}

export function CancelInvitationDialog({ companyUuid, uuid, email }: Props) {
  const queryClient = useQueryClient()

  const { mutate: cancel, isPending } = useCustomerCompanyInvitationDestroy({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getCustomerCompanyInvitationListQueryKey(companyUuid),
        })
        queryClient.invalidateQueries({
          queryKey: getCustomerMembershipActivityListQueryKey(companyUuid),
        })
        toast.success(`Invitation for ${email} cancelled`)
      },
      onError: () => {
        toast.error("Unable to cancel the invitation")
      },
    },
  })

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={isPending}
          aria-label={`Cancel the invitation for ${email}`}
          title="Cancel invite"
        >
          {isPending ? (
            <LoaderCircleIcon className="animate-spin" />
          ) : (
            <MailXIcon className="text-destructive" />
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel invite</AlertDialogTitle>
          <AlertDialogDescription>
            Cancel invite - they won&apos;t be able to join unless invited
            again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep invite</AlertDialogCancel>
          <AlertDialogAction onClick={() => cancel({ companyUuid, uuid })}>
            Cancel invite for {email}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
